import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
	Button,
	Label,
	NoAutoFillInput,
	CheckboxToggle,
	ActionButton,
	Input,
	InputContainer,
	ButtonsContainer,
	RequiredMarker,
	TextArea,
} from "@components";
import { QueryState } from "../../../hooks/useFetchedData";
import Routes from "@service/navigation/routes";
import { NewDivision, newDivisionSchema } from "@models/Division";
import { DivisionStatus } from "@models/DivisionStatus";
import JobRadSettings from "./JobRadSettings";
import Customer, { CustomerStatus } from "@models/Customer";
import { zodResolver } from "@hookform/resolvers/zod";

interface Props {
	customer: Customer;
	onSubmit: (division: NewDivision) => void;
	cmdState: QueryState<any>;
}

const statusMap = {
	[CustomerStatus.Trial]: DivisionStatus.Trial,
	[CustomerStatus.Active]: DivisionStatus.Active,
	[CustomerStatus.Archived]: DivisionStatus.Archived,
	[CustomerStatus.Deactivated]: DivisionStatus.Deactivated,
};

function DivisionForm({ onSubmit, cmdState, customer }: Props) {
	const [isJobRad, setIsJobRad] = useState(false);
	const initialStatus = statusMap[customer.status];
	const isReadOnlyCustomer = [
		CustomerStatus.Archived,
		CustomerStatus.Deactivated,
	].includes(customer.status);

	const defaultValues: NewDivision = {
		name: "",
		street: "",
		phone: "",
		zip: "",
		city: "",
		fax: "",
		country: "",
		stid: "",
		ustid: "",
		isMainDivision: false,
		status: initialStatus,
		comment: "",
		providerSettings: {},
		isSachbezugFitnessEnabled: false,
	};

	const form = useForm<NewDivision>({
		mode: "onChange",
		defaultValues,
		resolver: zodResolver(newDivisionSchema),
	});

	const {
		control,
		formState,
		register,
		resetField,
		handleSubmit,
		setValue,
		watch,
	} = form;

	const isTrialDivision = watch("status") === DivisionStatus.Trial;

	const toggleJobRadSetting = () => {
		setIsJobRad((preVal) => {
			if (preVal) {
				resetField("providerSettings", { defaultValue: {} });
			}
			return !preVal;
		});
	};

	const toggleTrialStatus = () => {
		setValue(
			"status",
			isTrialDivision ? DivisionStatus.Active : DivisionStatus.Trial
		);
	};

	const handleOnSubmit = (data: NewDivision) => {
		onSubmit(data);
	};

	return (
		<FormProvider {...form}>
			<FormContainer className="form" onSubmit={handleSubmit(handleOnSubmit)}>
				<FlexRowContainer>
					<ColumnContainer>
						<InputContainer>
							<Label htmlFor="name">
								Name <RequiredMarker />
							</Label>
							<Input
								id="name"
								as={NoAutoFillInput}
								{...register("name")}
								data-testid="division-form-name"
							/>
						</InputContainer>
						<InputContainer>
							<Label htmlFor="phone">Telefon</Label>
							<Input
								id="phone"
								as={NoAutoFillInput}
								{...register("phone")}
								data-testid="division-form-phone"
							/>
						</InputContainer>
						<InputContainer>
							<Label htmlFor="fax">Telefax</Label>
							<Input
								id="fax"
								as={NoAutoFillInput}
								{...register("fax")}
								data-testid="division-form-fax"
							/>
						</InputContainer>
						<InputContainer>
							<Label htmlFor="stid">Steuernummer</Label>
							<Input
								id="stid"
								as={NoAutoFillInput}
								{...register("stid")}
								data-testid="division-form-stid"
							/>
						</InputContainer>
					</ColumnContainer>
					<ColumnContainer>
						<InputContainer>
							<Label htmlFor="street">Straße</Label>
							<Input
								id="street"
								as={NoAutoFillInput}
								control={control}
								{...register("street")}
								data-testid="division-form-street"
							/>
						</InputContainer>
						<FlexRowContainer>
							<InputContainer>
								<Label htmlFor="zip">PLZ</Label>
								<Input
									id="zip"
									as={NoAutoFillInput}
									{...register("zip")}
									data-testid="division-form-zip"
								/>
							</InputContainer>
							<InputContainer>
								<Label htmlFor="city">Ort</Label>
								<Input
									id="city"
									as={NoAutoFillInput}
									{...register("city")}
									data-testid="division-form-city"
								/>
							</InputContainer>
						</FlexRowContainer>
						<InputContainer>
							<Label htmlFor="country">Land</Label>
							<Input
								id="country"
								as={NoAutoFillInput}
								{...register("country")}
								data-testid="division-form-country"
							/>
						</InputContainer>
						<InputContainer>
							<Label htmlFor="ustid">Umsatzsteuer-Identifikationsnummer</Label>
							<Input
								id="ustid"
								as={NoAutoFillInput}
								{...register("ustid")}
								data-testid="test-ustid"
							/>
						</InputContainer>
					</ColumnContainer>
				</FlexRowContainer>
				<FlexRowContainer>
					<CheckboxToggle
						label="Ist Hauptsitz"
						data-testid="division-form-isMainDivision"
						{...register("isMainDivision")}
					/>
					<CheckboxToggle
						label="Ist Trialstandort"
						data-testid="division-form-isTrialDivision"
						onChange={toggleTrialStatus}
						checked={isTrialDivision}
					/>
					<CheckboxToggle label="Nutzt JobRad" onChange={toggleJobRadSetting} />
				</FlexRowContainer>
				{isJobRad && <JobRadSettings />}
				<InputContainer>
					<Label htmlFor="comment">Kommentar</Label>
					<TextArea
						id="comment"
						as={NoAutoFillInput}
						{...register("comment")}
						data-testid="division-form-comment"
					/>
				</InputContainer>

				<ButtonsContainer>
					<ActionButton
						type="submit"
						disabled={
							!formState.isValid || !formState.isDirty || isReadOnlyCustomer
						}
						data-testid="update-division-button"
						query={cmdState}
					>
						Speichern
					</ActionButton>
					<Link to={Routes.Customers.makePath()}>
						<Button type="submit" data-testid="to-back-button">
							Zurück
						</Button>
					</Link>
				</ButtonsContainer>
			</FormContainer>
		</FormProvider>
	);
}

const FlexRowContainer = styled.div`
	display: flex;
	flex-direction: row;
	column-gap: 2rem;
`;

const FormContainer = styled.form`
	max-width: 1100px;
	display: flex;
	flex-direction: column;
	row-gap: 1rem;
	margin-inline: auto;
`;
const ColumnContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	row-gap: 1rem;
`;

export default DivisionForm;
